import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  TrashIcon,
  KeyIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getUserTypeLabel, getUserTypeBadgeClasses } from "../utils/users";
import { deleteUser as deleteUserApi } from "../utils/usersApi";
import { pushLibraryAdminToZoho } from "../utils/zohoSync";
import ModalDialog from "../components/ModalDialog";

const NO_ORG = { id: "", name: "— No organization —" };

function Users() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgFilterQuery, setOrgFilterQuery] = useState("");

  // Update User dialog state
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Delete User dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newUserType, setNewUserType] = useState("");
  const [newOrganizationId, setNewOrganizationId] = useState("");
  const [dialogOrgComboQuery, setDialogOrgComboQuery] = useState("");

  // Fetch organizations for the filter dropdown
  useEffect(() => {
    const fetchOrganizations = async () => {
      setOrgsLoading(true);
      try {
        const orgsRef = collection(db, "organizations");
        const q = query(orgsRef, orderBy("name"));
        const snapshot = await getDocs(q);
        const orgsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setOrganizations(orgsList);

        // Check for URL parameter to auto-filter by organization
        const urlParams = new URLSearchParams(location.search);
        const orgParam = urlParams.get("org");
        if (orgParam) {
          setSelectedOrg(orgParam);
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Error loading organizations");
      } finally {
        setOrgsLoading(false);
      }
    };

    fetchOrganizations();
  }, [location.search]);

  // Use useMemo for efficient filtering without state updates
  const filteredUsers = useMemo(() => {
    if (!users.length) return [];

    let filtered = users;

    // Filter by email
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by organization
    if (selectedOrg && selectedOrg !== "") {
      filtered = filtered.filter((user) => {
        if (user.organization_id && user.organization_id.path) {
          const orgId = user.organization_id.path.split("/").pop();
          return orgId === selectedOrg;
        }
        return false;
      });
    }

    // Only return results if there are active filters
    if (searchTerm.trim() !== "" || (selectedOrg && selectedOrg !== "")) {
      return filtered;
    } else {
      return []; // Show empty list when no filters
    }
  }, [users, searchTerm, selectedOrg]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, "users");
        const q = query(usersRef, orderBy("created_time", "desc"));
        const querySnapshot = await getDocs(q);

        const usersList = querySnapshot.docs.map((docSnap) => {
          const userData = docSnap.data();
          return {
            id: docSnap.id,
            ...userData,
            // Remove organizationName since we're not using it anymore
          };
        });

        setUsers(usersList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Error loading users");
        setLoading(false);
      }
    };

    fetchUsers();
  }, [selectedOrg]); // Removed applyFilters dependency since we're using useMemo now

  const handleSearch = (value) => {
    setSearchTerm(value);
    // Remove the direct call to applyFilters - let the useEffect handle it
  };

  // Get organization name for display
  const getOrganizationName = (user) => {
    if (!user.organization_id || !user.organization_id.path) return "--";
    const orgId = user.organization_id.path.split("/").pop();
    const org = organizations.find((o) => o.id === orgId);
    return org ? org.name : "--";
  };

  const orgFilterOptions = useMemo(() => {
    const q = orgFilterQuery.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter((org) => org.name.toLowerCase().includes(q));
  }, [organizations, orgFilterQuery]);

  const filterComboValue = useMemo(() => {
    if (!selectedOrg) return null;
    return (
      organizations.find((o) => o.id === selectedOrg) || {
        id: selectedOrg,
        name: "",
      }
    );
  }, [selectedOrg, organizations]);

  const dialogComboOptions = useMemo(() => {
    const q = dialogOrgComboQuery.trim().toLowerCase();
    const filtered = q
      ? organizations.filter((org) => org.name.toLowerCase().includes(q))
      : organizations;
    return [NO_ORG, ...filtered];
  }, [organizations, dialogOrgComboQuery]);

  const dialogComboValue = useMemo(() => {
    if (!newOrganizationId) return NO_ORG;
    return (
      organizations.find((o) => o.id === newOrganizationId) || {
        id: newOrganizationId,
        name: dialogOrgComboQuery || "",
      }
    );
  }, [newOrganizationId, organizations, dialogOrgComboQuery]);

  const handleResetSearch = () => {
    setSearchTerm("");
    setSelectedOrg("");
    setOrgFilterQuery("");
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteUserApi(userToDelete.id);

      // Update local state
      const updatedUsers = users.filter((u) => u.id !== userToDelete.id);
      setUsers(updatedUsers);

      setShowDeleteDialog(false);
      setUserToDelete(null);
      toast.success("User deleted successfully!");
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.message || "Error deleting user");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleResetPassword = (user) => {
    // TODO: Implement password reset functionality
    toast.error("Password reset functionality not yet implemented");
  };

  const handleChangeUserType = (user) => {
    setSelectedUser(user);
    setNewUserType(user.user_type || "user");
    const currentOrgId = user.organization_id?.path
      ? user.organization_id.path.split("/").pop()
      : "";
    setNewOrganizationId(currentOrgId);
    setDialogOrgComboQuery("");
    setShowTypeDialog(true);
  };

  const handleSaveUserType = async () => {
    if (!selectedUser || !newUserType) return;

    try {
      const promotedUid = selectedUser.id;
      const userRef = doc(db, "users", selectedUser.id);

      const updates = {
        user_type: newUserType,
        organization_id: newOrganizationId
          ? doc(db, "organizations", newOrganizationId)
          : null,
        organization_name: newOrganizationId
          ? organizations.find((o) => o.id === newOrganizationId)?.name || ""
          : "",
      };

      await updateDoc(userRef, updates);

      // Update local state
      const updatedUserData = {
        ...selectedUser,
        user_type: newUserType,
        organization_id: updates.organization_id,
        organization_name: updates.organization_name,
      };
      const updatedUsers = users.map((user) =>
        user.id === selectedUser.id ? updatedUserData : user
      );
      setUsers(updatedUsers);

      setShowTypeDialog(false);
      setSelectedUser(null);
      setNewUserType("");
      setNewOrganizationId("");
      setDialogOrgComboQuery("");
      toast.success("User updated successfully!");

      // Library admins only: push to Zoho whenever role is saved as admin (promotion or org update).
      if (newUserType === "admin") {
        try {
          const syncResult = await pushLibraryAdminToZoho(promotedUid);
          const c = syncResult?.counts || {};
          const failed = c.failed ?? (syncResult?.errors?.length || 0);
          const pushed = c.pushed ?? (syncResult?.ok?.length || 0);
          const skipped = c.skipped ?? (syncResult?.skipped?.length || 0);
          if (failed > 0) {
            toast.error(
              `Zoho CRM: sync failed for this admin (${syncResult?.errors?.[0]?.error || "see server logs"}).`,
              { duration: 8000 },
            );
          } else if (pushed > 0) {
            toast.success("Zoho CRM: admin contact updated.", {
              duration: 5000,
            });
          } else if (skipped > 0) {
            toast(
              `Zoho CRM: skipped — ${syncResult?.skipped?.[0]?.reason || "check org HRF ID and Zoho account"}.`,
              { duration: 8000 },
            );
          }
        } catch (zohoErr) {
          console.error("Zoho CRM sync after user update failed:", zohoErr);
          toast.error(
            zohoErr.message || "Zoho CRM sync failed after saving admin.",
            { duration: 8000 },
          );
        }
      }
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Error updating user");
    }
  };

  const handleCancelUserType = () => {
    setShowTypeDialog(false);
    setSelectedUser(null);
    setNewUserType("");
    setNewOrganizationId("");
    setDialogOrgComboQuery("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading users...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">Users</h1>
          </div>

          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Email Search */}
              <div className="flex-1 max-w-md">
                <label htmlFor="users-email-search" className="sr-only">
                  Search users by email
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                    aria-hidden="true"
                  />
                  <input
                    id="users-email-search"
                    type="text"
                    placeholder="Search users by email..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Organization Filter */}
              <div className="w-full sm:w-80 relative">
                <span id="users-org-filter-label" className="sr-only">
                  Filter by organization
                </span>
                <Combobox
                  value={filterComboValue}
                  onChange={(org) => {
                    if (!org) {
                      setSelectedOrg("");
                      setOrgFilterQuery("");
                      return;
                    }
                    setSelectedOrg(org.id);
                    setOrgFilterQuery("");
                  }}
                  by={(a, b) => a.id === b.id}
                  disabled={orgsLoading}
                >
                  <div className="relative">
                    <ComboboxInput
                      aria-labelledby="users-org-filter-label"
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                      displayValue={(item) => item?.name ?? ""}
                      onChange={(e) => setOrgFilterQuery(e.target.value)}
                      placeholder="Search organizations..."
                      autoComplete="off"
                    />
                    {selectedOrg ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedOrg("");
                          setOrgFilterQuery("");
                        }}
                        className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                        aria-label="Clear organization filter"
                      >
                        ×
                      </button>
                    ) : null}
                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded">
                      <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
                    </ComboboxButton>
                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white py-1 shadow-lg empty:invisible">
                      {orgFilterOptions.length === 0 ? (
                        <div className="px-4 py-2 text-gray-500">
                          No organizations found
                        </div>
                      ) : (
                        orgFilterOptions.map((org) => (
                          <ComboboxOption
                            key={org.id}
                            value={org}
                            className="cursor-pointer px-4 py-2 data-[focus]:bg-gray-100 border-b border-gray-100 last:border-b-0"
                          >
                            {org.name}
                          </ComboboxOption>
                        ))
                      )}
                    </ComboboxOptions>
                  </div>
                </Combobox>
              </div>

              {/* Reset Button */}
              {(searchTerm || selectedOrg) && (
                <button
                  type="button"
                  onClick={handleResetSearch}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUserTypeBadgeClasses(
                          user.user_type
                        )}`}
                      >
                        {getUserTypeLabel(user.user_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getOrganizationName(user)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleChangeUserType(user)}
                          className="text-blue-600 hover:text-blue-900 transition-colors p-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                          aria-label={`Update user ${user.email}`}
                        >
                          <PencilIcon className="w-5 h-5" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResetPassword(user)}
                          className="text-yellow-600 hover:text-yellow-900 transition-colors p-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                          aria-label={`Reset password for ${user.email}`}
                        >
                          <KeyIcon className="w-5 h-5" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-900 transition-colors p-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                          aria-label={`Delete user ${user.email}`}
                        >
                          <TrashIcon className="w-5 h-5" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm || selectedOrg
                  ? "No users found matching your search criteria."
                  : "Use the search and filter options above to find users."}
              </p>
            </div>
          )}
        </div>
      </div>

      <ModalDialog
        open={showTypeDialog}
        onClose={handleCancelUserType}
        title="Update User"
        contentAlign="start"
        panelClassName="w-[28rem] border p-5 shadow-lg"
      >
        <p className="text-sm text-gray-600 mb-4 mt-2">
          Updating: <strong>{selectedUser?.email}</strong>
        </p>

        <div className="mb-4">
          <label
            htmlFor="users-dialog-user-type"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            User Type
          </label>
          <select
            id="users-dialog-user-type"
            value={newUserType}
            onChange={(e) => setNewUserType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>

        <div className="mb-4 relative">
          <span
            id="users-dialog-org-label"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Organization
          </span>
          <Combobox
            value={dialogComboValue}
            onChange={(org) => {
              if (!org || org.id === "") {
                setNewOrganizationId("");
                setDialogOrgComboQuery("");
                return;
              }
              setNewOrganizationId(org.id);
              setDialogOrgComboQuery("");
            }}
            by={(a, b) => a.id === b.id}
            disabled={orgsLoading}
          >
            <div className="relative">
              <ComboboxInput
                aria-labelledby="users-dialog-org-label"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                displayValue={(item) => item?.name ?? ""}
                onChange={(e) => setDialogOrgComboQuery(e.target.value)}
                placeholder="Search organizations..."
                autoComplete="off"
              />
              {newOrganizationId ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setNewOrganizationId("");
                    setDialogOrgComboQuery("");
                  }}
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  aria-label="Clear organization selection"
                >
                  ×
                </button>
              ) : null}
              <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
              </ComboboxButton>
              <ComboboxOptions className="absolute z-[60] mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-300 bg-white py-1 shadow-lg empty:invisible">
                {dialogComboOptions.length === 0 ? (
                  <div className="px-4 py-2 text-gray-500">
                    No organizations found
                  </div>
                ) : (
                  dialogComboOptions.map((org) => (
                    <ComboboxOption
                      key={org.id === "" ? "__none" : org.id}
                      value={org}
                      className="cursor-pointer px-4 py-2 data-[focus]:bg-gray-100 border-b border-gray-100 last:border-b-0"
                    >
                      {org.name}
                    </ComboboxOption>
                  ))
                )}
              </ComboboxOptions>
            </div>
          </Combobox>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancelUserType}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveUserType}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            Save
          </button>
        </div>
      </ModalDialog>

      <ModalDialog
        open={showDeleteDialog && !!userToDelete}
        onClose={() => {
          if (!deleteLoading) handleCancelDelete();
        }}
        role="alertdialog"
        title="Delete User"
        contentAlign="start"
        panelClassName="w-[28rem] border p-5 shadow-lg"
      >
        <p className="text-sm text-gray-600 mb-4 mt-2">
          Are you sure you want to delete{" "}
          <strong>{userToDelete?.email}</strong>?
        </p>
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
          This will remove the user from both the database and Firebase
          Authentication. This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteLoading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </ModalDialog>
    </div>
  );
}

export default Users;
